import React, { useCallback } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import Button from '@atlaskit/button/new';

import type { TrackType } from '../../../../api/cc-smarts/models/TrackType';
import type { ActiveContent } from '../../../../types/activeContent';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { useContextMenuActions } from '../../../hooks/useContextMenuActions';
import { setSupportingContent } from '../../../state/AudioPlayerState';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';


import type { RefetchAudioButtonQuery } from './__generated__/RefetchAudioButtonQuery.graphql';


export type RefetchAudioButtonProps = {
	buttonText: MessageDescriptor;
	source: 'customizePanel' | 'sourcesPanel';
	shouldRequeue?: boolean;
	supportingContent?: ActiveContent['supportingContent'];
	isDisabled?: boolean;
};

/**
 * Button component that handles refetching audio with optional supporting content updates
 * and playlist management.
 */
export const RefetchAudioButton = ({
	buttonText,
	shouldRequeue = false,
	supportingContent = [],
	isDisabled = false,
}: RefetchAudioButtonProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { cloudId, activeContent } = useAudioPlayerStore();
	const environment = useRelayEnvironment();
	const { removeTrack, addTrack } = useContextMenuActions(cloudId);
	const isLoading = streamingState.state === AudioStreamingState.LOADING;
	const { formatMessage } = useIntl();


	const startNewAudio = useCallback((overrideSupportingContent?: string[]) => {
		// TODO: Add analytics
		setSupportingContent(supportingContent);
		fetchAudio(overrideSupportingContent);
	}, [fetchAudio, supportingContent]);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	/** Handles requeuing the current track with updated supporting content */
	const handleRequeueTrack = useCallback(async () => {
		try {
			const playlist = await fetchQuery<RefetchAudioButtonQuery>(
				environment,
				graphql`
					query RefetchAudioButtonQuery($cloudId: ID!) {
						confluence_getPlaylist(cloudId: $cloudId) {
							tracks {
								nodes {
									mainSource
									supportingSources
									type
									mainContent {
										id
									}
								}
							}
						}
					}
				`,
				{ cloudId },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();

			if (playlist?.confluence_getPlaylist?.tracks?.nodes) {
				const activeTrackIsInPlaylist =
					playlist.confluence_getPlaylist.tracks.nodes[0]?.mainContent?.id ===
					activeContent.contentId;

				if (activeTrackIsInPlaylist) {
					const currentTrack = playlist.confluence_getPlaylist.tracks.nodes[0];
					const trackCopy = { ...currentTrack };

					await removeTrack(
						currentTrack.mainSource,
						[...(currentTrack.supportingSources ?? [])],
						currentTrack.type as TrackType,
					);

					await addTrack(
						trackCopy.mainSource,
						supportingContent.map((resource) => resource.ari),
						trackCopy.type as TrackType,
					);
				}
			}
		} catch (error) {
			// TODO: Add proper error handling - consider showing user feedback
		}
	}, [cloudId, environment, removeTrack, addTrack, activeContent, supportingContent]);

	const handleClick = useCallback(async () => {
		stopExistingAudio();

		if (supportingContent.length > 0) {
			setSupportingContent(supportingContent);
		}

		if (shouldRequeue) {
			await handleRequeueTrack();
		}

		// Pass the latest supporting content directly to fetchAudio
		const latestSupportingARIs = supportingContent.map((resource) => resource.ari);
		startNewAudio(latestSupportingARIs.length > 0 ? latestSupportingARIs : undefined);
	}, [startNewAudio, stopExistingAudio, handleRequeueTrack, shouldRequeue, supportingContent]);

	return (
		<Button
			testId="refetch-audio-button"
			shouldFitContainer
			appearance="primary"
			onClick={handleClick}
			isLoading={isLoading}
			isDisabled={isDisabled}
		>
			{formatMessage(buttonText)}
		</Button>
	);
};
